import { IDiaryComment } from "api/interfaces/profile.interface";

// 제네릭 댓글 타입 (공통 필드만 포함)
interface BaseComment {
    id: number;
    childList?: any[];
}

/**
 * 댓글 목록에서 특정 commentId를 가진 댓글(부모/자식 포함)을 찾아서 업데이트합니다.
 * @param comments 댓글 목록
 * @param commentId 업데이트할 댓글 ID
 * @param updates 업데이트할 데이터
 * @returns 업데이트된 댓글 목록
 */
export const updateCommentById = <T extends BaseComment>(
    comments: T[], 
    commentId: number, 
    updates: Partial<T>
): T[] => {
    return comments.map(comment => {
        // 현재 댓글이 찾는 ID와 일치하는 경우
        if (comment.id === commentId) {
            return { ...comment, ...updates };
        }
        
        // 자식 댓글들 중에서 찾는 경우
        if (comment.childList && comment.childList.length > 0) {
            const updatedChildList = comment.childList.map(childComment => 
                childComment.id === commentId 
                    ? { ...childComment, ...updates }
                    : childComment
            );
            
            // 자식 댓글 중 하나가 업데이트되었는지 확인
            const hasUpdatedChild = updatedChildList.some((child, index) => 
                child !== comment.childList![index]
            );
            
            if (hasUpdatedChild) {
                return { ...comment, childList: updatedChildList };
            }
        }
        
        return comment;
    });
};

/**
 * 댓글 목록에서 특정 commentId를 가진 댓글(부모/자식 포함)을 찾아서 제거합니다.
 * @param comments 댓글 목록
 * @param commentId 제거할 댓글 ID
 * @returns 업데이트된 댓글 목록
 */
export const removeCommentById = <T extends BaseComment>(
    comments: T[], 
    commentId: number
): T[] => {
    return comments
        .filter(comment => comment.id !== commentId) // 부모 댓글 제거
        .map(comment => {
            // 자식 댓글들 중에서 제거
            if (comment.childList && comment.childList.length > 0) {
                const updatedChildList = comment.childList.filter(childComment => 
                    childComment.id !== commentId
                );
                
                return { ...comment, childList: updatedChildList };
            }
            
            return comment;
        });
};

/**
 * 댓글 목록에서 특정 commentId를 가진 댓글(부모/자식 포함)을 찾습니다.
 * @param comments 댓글 목록
 * @param commentId 찾을 댓글 ID
 * @returns 찾은 댓글 객체 또는 null
 */
export const findCommentById = <T extends BaseComment>(
    comments: T[], 
    commentId: number
): T | null => {
    // 부모 댓글 중에서 찾기
    const parentComment = comments.find(comment => comment.id === commentId);
    if (parentComment) {
        return parentComment;
    }
    
    // 자식 댓글들 중에서 찾기
    for (const comment of comments) {
        if (comment.childList && comment.childList.length > 0) {
            const childComment = comment.childList.find(child => child.id === commentId);
            if (childComment) {
                return childComment;
            }
        }
    }
    
    return null;
};

/**
 * 댓글 좋아요 토글 결과를 적용합니다.
 * @param comments 댓글 목록
 * @param commentId 좋아요 토글할 댓글 ID
 * @param likeResult 좋아요 토글 결과 { isLiked: boolean, likeCount: number }
 * @returns 업데이트된 댓글 목록
 */
export const updateCommentLike = <T extends BaseComment & { isLiked?: boolean; likeCount?: number }>(
    comments: T[], 
    commentId: number, 
    likeResult: { isLiked: boolean; likeCount: number }
): T[] => {
    return updateCommentById<T>(comments, commentId, {
        isLiked: likeResult.isLiked,
        likeCount: likeResult.likeCount
    } as Partial<T>);
};

/**
 * 새 댓글을 추가합니다.
 * @param comments 현재 댓글 목록
 * @param newComment 새로 추가할 댓글
 * @param parentId 부모 댓글 ID (대댓글인 경우)
 * @returns 업데이트된 댓글 목록
 */
export const addComment = <T extends BaseComment>(
    comments: T[], 
    newComment: T, 
    parentId?: number
): T[] => {
    if (parentId) {
        // 대댓글인 경우: 해당 부모 댓글의 childList에 추가
        return comments.map(comment =>
            comment.id === parentId
                ? {
                    ...comment,
                    childList: [...(comment.childList || []), newComment]
                }
                : comment
        );
    } else {
        // 일반 댓글인 경우: 최상단에 추가
        return [newComment, ...comments];
    }
};
